// src/admin/components/AIChatWidget.tsx
// Floating AI assistant for admin staff, visible on every admin page. Calls
// the `chatWithAI` Cloud Function (Google Gemini) — see
// functions/functions/functions/src/chat.ts.
import React, { useEffect, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  Box,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
  Zoom,
} from '@mui/material';
import { Close as CloseIcon, Send as SendIcon, SmartToy as BotIcon, ChatBubble as ChatIcon } from '@mui/icons-material';
import { functions } from '../../firebase/config';
import { useCompany } from '../../contexts/CompanyContext';

interface ChatMessage {
  role: 'user' | 'model';
  text: string;
}

const chatWithAI = httpsCallable<
  { message: string; history: ChatMessage[]; audience: 'admin'; companyName: string },
  { reply: string }
>(functions, 'chatWithAI');

const AIChatWidget: React.FC = () => {
  const { companyProfile } = useCompany();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', text }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    setError('');

    try {
      const result = await chatWithAI({
        message: text,
        history: messages,
        audience: 'admin',
        companyName: companyProfile?.companyName || 'the dealership',
      });
      setMessages([...nextMessages, { role: 'model', text: result.data.reply }]);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box sx={{ position: 'fixed', bottom: { xs: 56, sm: 64 }, right: { xs: 16, sm: 24 }, zIndex: (t) => t.zIndex.drawer + 300 }}>
      <Zoom in={open}>
        <Paper
          elevation={8}
          sx={{
            width: 340,
            maxWidth: 'calc(100vw - 48px)',
            height: 460,
            maxHeight: 'calc(100vh - 140px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 3,
            mb: 1.75,
            position: 'absolute',
            bottom: 52,
            right: 0,
          }}
        >
          <Box sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BotIcon fontSize="small" />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Assistant
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: 1.75, py: 1.5 }}>
            {messages.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
                Ask how to use a feature, find a page, or general dealership questions.
              </Typography>
            )}
            {messages.map((m, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', mb: 1.25 }}>
                <Box
                  sx={{
                    maxWidth: '82%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    fontSize: 13.5,
                    lineHeight: 1.45,
                    whiteSpace: 'pre-wrap',
                    bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                    color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  {m.text}
                </Box>
              </Box>
            ))}
            {sending && (
              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5 }}>Typing…</Typography>
            )}
            {error && (
              <Typography variant="caption" color="error" sx={{ display: 'block', px: 0.5 }}>{error}</Typography>
            )}
          </Box>

          <Box component="form" onSubmit={handleSend} sx={{ display: 'flex', gap: 1, p: 1, borderTop: 1, borderColor: 'divider' }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Type a message…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 5 } }}
            />
            <IconButton type="submit" color="primary" disabled={sending || !input.trim()}>
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>
      </Zoom>

      <Fab color="primary" size="small" onClick={() => setOpen((v) => !v)} aria-label={open ? 'Close assistant' : 'Open assistant'}>
        {open ? <CloseIcon /> : <ChatIcon />}
      </Fab>
    </Box>
  );
};

export default AIChatWidget;
